import { useEffect, useRef, useReducer, useCallback, useState } from "react";
import { getLiveSocket } from "../../lib/socket.js";
import { toLocalPhaseEnd } from "../../lib/server-clock.js";
import { CountdownRing } from "../../components/CountdownRing.js";
import { OptionShape } from "../../components/OptionShape.js";
import { useT, type Translate } from "../../i18n/index.js";
import type { Socket } from "socket.io-client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AnswerOption {
  id: string;
  label: string;
}

interface QuestionEvent {
  questionRunId: string;
  type: string;
  prompt: string;
  imageAssetId?: string | null;
  timerSeconds: number;
  orderIndex: number;
  options: AnswerOption[];
}

interface PlayerResultEvent {
  isCorrect: boolean;
  points: number;
  maxPoints: number;
  runningTotal: number;
  streak?: number;
  streakBonus?: number;
}

interface PodiumEntry {
  rank: number;
  playerId: string;
  nickname: string;
  score: number;
}

type GamePhase =
  | "question_intro"
  | "answering"
  | "answer_reveal"
  | "leaderboard"
  | "podium"
  | "ended"
  | "aborted";

interface GameState {
  phase: GamePhase;
  question: QuestionEvent | null;
  submittedOptions: string[] | null;
  submittedText: string | null;
  result: PlayerResultEvent | null;
  podium: PodiumEntry[];
  phaseEndsAt: number | null;
  /**
   * Running total carried across questions. `result` is cleared when the next
   * question starts, so the score cannot be derived from it without the badge
   * dropping back to zero for the whole of every question.
   */
  score: number;
}

type Action =
  | { type: "STATE_CHANGED"; phase: GamePhase; phaseEndsAt: number | null }
  | { type: "QUESTION_STARTED"; question: QuestionEvent }
  | { type: "ANSWER_ACCEPTED"; submittedOptions: string[]; submittedText: string | null }
  | { type: "RESULT"; result: PlayerResultEvent }
  | { type: "PODIUM"; entries: PodiumEntry[] }
  | { type: "SCORE_SYNCED"; score: number };

function reducer(state: GameState, action: Action): GameState {
  switch (action.type) {
    case "STATE_CHANGED":
      return {
        ...state,
        phase: action.phase,
        phaseEndsAt: action.phaseEndsAt,
      };
    case "QUESTION_STARTED":
      return {
        ...state,
        question: action.question,
        submittedOptions: null,
        submittedText: null,
        result: null,
        phase: "question_intro",
      };
    case "ANSWER_ACCEPTED":
      return {
        ...state,
        submittedOptions: action.submittedOptions,
        submittedText: action.submittedText,
      };
    case "RESULT":
      return {
        ...state,
        result: action.result,
        score: action.result.runningTotal,
        phase: "answer_reveal",
      };
    case "PODIUM":
      return { ...state, podium: action.entries, phase: "podium" };
    case "SCORE_SYNCED":
      return state.score === action.score ? state : { ...state, score: action.score };
    default:
      return state;
  }
}

const INITIAL: GameState = {
  phase: "question_intro",
  question: null,
  submittedOptions: null,
  submittedText: null,
  result: null,
  podium: [],
  phaseEndsAt: null,
  score: 0,
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

// Vivid option colors — white text on these backgrounds passes WCAG AA
const OPTION_COLORS = [
  "var(--opt-0)",
  "var(--opt-1)",
  "var(--opt-2)",
  "var(--opt-3)",
];

const s = {
  page: {
    minHeight: "100svh",
    background: "var(--bg)",
    color: "var(--text)",
    display: "flex",
    flexDirection: "column" as const,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "0.875rem var(--space-md)",
    background: "var(--surface)",
    borderBottom: "1px solid var(--border)",
    boxShadow: "var(--shadow-sm)",
  },
  headerName: {
    fontWeight: 700,
    fontSize: "var(--text-sm)",
    color: "var(--text-mid)",
    maxWidth: "60%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  },
  headerScore: {
    fontWeight: 800,
    fontSize: "var(--text-base)",
    color: "var(--brand)",
    background: "var(--brand-lt)",
    padding: "0.25rem 0.875rem",
    borderRadius: "2rem",
  },
  main: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as const,
    padding: "var(--space-md)",
    gap: "var(--space-md)",
    maxWidth: "520px",
    width: "100%",
    margin: "0 auto",
  },
  prompt: {
    margin: 0,
    fontSize: "var(--text-xl)",
    fontWeight: 700,
    textAlign: "center" as const,
    lineHeight: 1.35,
    padding: "var(--space-md) 0 var(--space-sm)",
    color: "var(--text)",
  },
  questionImage: {
    width: "100%",
    maxHeight: "220px",
    objectFit: "cover" as const,
    borderRadius: "var(--radius-md)",
    border: "1px solid var(--border)",
    boxShadow: "var(--shadow-sm)",
  },
  statusMsg: {
    textAlign: "center" as const,
    color: "var(--text-dim)",
    fontSize: "var(--text-sm)",
    padding: "var(--space-md) 0",
    fontWeight: 500,
  },
  // Selected keeps vivid color — white inset ring signals locked-in
  optionBtn: (color: string, selected: boolean, disabled: boolean) => ({
    background: color,
    color: "oklch(99% 0.006 70)",
    border: "3px solid transparent",
    borderRadius: "var(--radius-md)",
    padding: "1.25rem var(--space-md)",
    fontSize: "var(--text-base)",
    fontWeight: 700,
    cursor: disabled ? "default" : "pointer",
    minHeight: "80px",
    width: "100%",
    textAlign: "left" as const,
    display: "flex",
    alignItems: "center",
    gap: "0.875rem",
    opacity: selected ? 1 : (disabled && !selected ? 0.4 : 1),
    transition: `opacity var(--duration-fast) var(--ease-out), box-shadow var(--duration-fast) var(--ease-out)`,
    touchAction: "manipulation",
    boxShadow: selected
      ? `inset 0 0 0 3px oklch(99% 0.006 70 / 0.92), 0 4px 10px oklch(40% 0.01 65 / 0.25)`
      : `0 2px 6px oklch(40% 0.01 65 / 0.10)`,
  }),
  optionLetter: {
    flexShrink: 0,
    width: "2rem",
    height: "2rem",
    borderRadius: "0.375rem",
    background: "oklch(99% 0.006 70 / 0.24)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 800,
    fontSize: "1rem",
    lineHeight: 1,
  },
  optionCheck: (checked: boolean) => ({
    marginLeft: "auto",
    flexShrink: 0,
    width: "1.75rem",
    height: "1.75rem",
    borderRadius: "0.5rem",
    border: "2.5px solid oklch(99% 0.006 70 / 0.85)",
    background: checked ? "oklch(99% 0.006 70)" : "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background var(--duration-fast) var(--ease-out)",
  }),
  answerInput: {
    fontSize: "var(--text-lg)",
    padding: "1rem",
    borderRadius: "var(--radius-md)",
    border: "2px solid var(--border)",
    background: "var(--surface)",
    color: "var(--text)",
    width: "100%",
    minHeight: "64px",
  },
  submitBtn: {
    background: "var(--brand)",
    color: "oklch(99% 0.006 70)",
    border: "none",
    borderRadius: "var(--radius-md)",
    padding: "1rem var(--space-md)",
    fontSize: "var(--text-base)",
    fontWeight: 800,
    minHeight: "64px",
    width: "100%",
    cursor: "pointer",
    boxShadow: "var(--shadow-sm)",
  },
  disabledSubmitBtn: {
    opacity: 0.45,
    cursor: "default",
  },
  resultCard: {
    background: "var(--surface)",
    border: "2px solid var(--border)",
    borderRadius: "var(--radius-xl)",
    padding: "var(--space-xl) var(--space-lg)",
    textAlign: "center" as const,
    boxShadow: "var(--shadow-md)",
    animation: "pop-in 0.28s cubic-bezier(0.16, 1, 0.3, 1) both",
  },
  resultCorrect: {
    background: "var(--success-lt)",
    borderColor: "var(--success)",
  },
  resultIncorrect: {
    background: "var(--error-lt)",
    borderColor: "var(--error)",
  },
  resultLabel: (correct: boolean) => ({
    margin: "0 0 0.5rem",
    fontWeight: 800,
    fontSize: "var(--text-xl)",
    color: correct ? "var(--success)" : "var(--error)",
  }),
  resultPoints: {
    fontSize: "var(--text-2xl)",
    fontWeight: 800,
    color: "var(--brand)",
    margin: "0 0 0.25rem",
  },
  resultTotal: {
    color: "var(--text-mid)",
    fontSize: "var(--text-sm)",
    margin: 0,
  },
  podiumList: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "0.625rem",
    marginTop: "var(--space-sm)",
    listStyle: "none",
    padding: 0,
  },
  podiumRow: (rank: number) => {
    const medalBg = rank === 1
      ? "oklch(90% 0.08 80)"
      : rank === 2
      ? "oklch(88% 0.01 65)"
      : rank === 3
      ? "oklch(86% 0.09 50)"
      : "var(--surface)";
    const medalBorder = rank === 1
      ? "var(--brand)"
      : rank === 2
      ? "oklch(65% 0.04 65)"
      : rank === 3
      ? "oklch(62% 0.14 50)"
      : "var(--border)";
    return {
      display: "flex",
      alignItems: "center",
      gap: "var(--space-md)",
      padding: rank === 1 ? "1.125rem var(--space-md)" : "0.875rem var(--space-md)",
      background: medalBg,
      borderRadius: "var(--radius-md)",
      border: rank === 1 ? `2.5px solid var(--brand)` : `1.5px solid ${medalBorder}`,
      boxShadow: rank === 1 ? "var(--shadow-md)" : "var(--shadow-sm)",
    };
  },
  podiumRank: {
    fontWeight: 800,
    fontSize: "var(--text-base)",
    minWidth: "2rem",
    color: "var(--text-mid)",
  },
  podiumNickname: { flex: 1, fontWeight: 600 },
  podiumScore: { fontWeight: 700, color: "var(--brand)" },
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface LiveGameProps {
  sessionId: string;
  playerId: string;
  playerToken: string;
  nickname: string;
  initialScore?: number;
}

function configureSocketAuth(socket: Socket, auth: Record<string, string>): void {
  if (socket.connected && JSON.stringify(socket.auth ?? {}) !== JSON.stringify(auth)) {
    socket.disconnect();
  }
  socket.auth = auth;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LiveGame({
  sessionId,
  playerId,
  playerToken,
  nickname,
  initialScore = 0,
}: LiveGameProps) {
  const socketRef = useRef<Socket | null>(null);
  const [state, dispatch] = useReducer(reducer, { ...INITIAL, score: initialScore });
  const pendingAnswer = useRef<{ optionIds: string[]; text: string | null } | null>(null);

  const [isConnected, setIsConnected] = useState(false);

  // Score flash — animate the badge when points arrive
  const [flashScore, setFlashScore] = useState(false);
  const prevScoreRef = useRef<number>(initialScore);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const socket = getLiveSocket();
    socketRef.current = socket;
    configureSocketAuth(socket, { type: "player", playerToken, sessionId });

    function onConnect() {
      setIsConnected(true);
      socket.emit("player:joinRoom", {});
    }
    function onDisconnect() {
      // The in-flight submission is lost with the connection. Releasing it lets
      // the player retry; a duplicate is rejected server-side either way.
      pendingAnswer.current = null;
      setIsConnected(false);
    }
    function onConnectError() {
      pendingAnswer.current = null;
      setIsConnected(false);
    }
    function onSnapshot(data: {
      phase?: GamePhase;
      phaseEndsAt?: string | null;
      serverNow?: string;
      player?: { score?: number };
    }) {
      // The snapshot is the only place a reconnecting player learns the score
      // they built up before the page reloaded.
      if (typeof data.player?.score === "number") {
        dispatch({ type: "SCORE_SYNCED", score: data.player.score });
      }
      if (data.phase) {
        dispatch({
          type: "STATE_CHANGED",
          phase: data.phase,
          phaseEndsAt: toLocalPhaseEnd(data.phaseEndsAt, data.serverNow),
        });
      }
    }
    function onStateChanged(data: { phase: GamePhase; phaseEndsAt?: string | null; serverNow?: string }) {
      dispatch({
        type: "STATE_CHANGED",
        phase: data.phase,
        phaseEndsAt: toLocalPhaseEnd(data.phaseEndsAt, data.serverNow),
      });
    }
    function onQuestionStarted(data: QuestionEvent) {
      // A new question invalidates any answer still in flight for the old one.
      pendingAnswer.current = null;
      dispatch({ type: "QUESTION_STARTED", question: data });
    }
    function onTimer(data: { endsAt: string | null; serverNow?: string }) {
      dispatch({
        type: "STATE_CHANGED",
        phase: "answering",
        phaseEndsAt: toLocalPhaseEnd(data.endsAt, data.serverNow),
      });
    }
    function onAnswerAccepted() {
      if (pendingAnswer.current) {
        dispatch({
          type: "ANSWER_ACCEPTED",
          submittedOptions: pendingAnswer.current.optionIds,
          submittedText: pendingAnswer.current.text,
        });
        pendingAnswer.current = null;
      }
    }
    function onResult(data: PlayerResultEvent) {
      dispatch({ type: "RESULT", result: data });
    }
    function onPodium(data: { rankings: PodiumEntry[] }) {
      dispatch({ type: "PODIUM", entries: data.rankings });
    }
    // A rejected submission must release the in-flight slot, or the player is
    // locked out of answering for the rest of the game. The common case is
    // pressing an answer just as the timer closes the question.
    function onServerError() {
      pendingAnswer.current = null;
    }

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("connect_error", onConnectError);
    socket.on("error:event", onServerError);
    socket.on("session:snapshot", onSnapshot);
    socket.on("session:stateChanged", onStateChanged);
    socket.on("question:started", onQuestionStarted);
    socket.on("question:timer", onTimer);
    socket.on("player:answerAccepted", onAnswerAccepted);
    socket.on("player:result", onResult);
    socket.on("session:podium", onPodium);

    if (socket.connected) onConnect();
    else socket.connect();

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("connect_error", onConnectError);
      socket.off("error:event", onServerError);
      socket.off("session:snapshot", onSnapshot);
      socket.off("session:stateChanged", onStateChanged);
      socket.off("question:started", onQuestionStarted);
      socket.off("question:timer", onTimer);
      socket.off("player:answerAccepted", onAnswerAccepted);
      socket.off("player:result", onResult);
      socket.off("session:podium", onPodium);
    };
  }, [sessionId, playerId, playerToken]);

  const submitAnswer = useCallback((optionIds: string[], text: string | null = null) => {
    if (
      !isConnected ||
      state.phase !== "answering" ||
      state.submittedOptions !== null ||
      pendingAnswer.current !== null
    ) {
      return;
    }
    const inFlight = { optionIds, text };
    pendingAnswer.current = inFlight;
    socketRef.current?.emit(
      "player:submitAnswer",
      { optionIds, ...(text !== null ? { text } : {}) },
      (response: unknown) => {
        // Release the slot on rejection so the player can try again. Guarded on
        // identity: a late ack must not clear a newer submission.
        const rejected =
          typeof response !== "object" || response === null || "error" in response;
        if (rejected && pendingAnswer.current === inFlight) {
          pendingAnswer.current = null;
        }
      },
    );
  }, [isConnected, state.phase, state.submittedOptions]);

  const t = useT();
  const currentScore = state.score;

  useEffect(() => {
    if (currentScore !== prevScoreRef.current) {
      prevScoreRef.current = currentScore;
      setFlashScore(true);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      flashTimerRef.current = setTimeout(() => setFlashScore(false), 400);
    }
    return () => {
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    };
  }, [currentScore]);

  return (
    <div className="player-game-page" style={s.page}>
      {!isConnected && (
        <div
          role="alert"
          className="player-connection-banner"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            padding: "0.5rem 1rem",
            background: "var(--error, #c0392b)",
            color: "oklch(99% 0.006 70)",
            textAlign: "center",
            fontSize: "var(--text-sm, 0.875rem)",
            fontWeight: 600,
            zIndex: 999,
          }}
        >
          {t("play.connectionLost")}
        </div>
      )}
      <header className="player-game-header" style={s.header} role="banner">
        <span className="player-game-name" style={s.headerName}>{nickname}</span>
        <span
          className="player-score-pill"
          style={{
            ...s.headerScore,
            ...(flashScore
              ? { background: "var(--brand)", color: "oklch(99% 0.006 70)", transform: "scale(1.08)",
                  transition: "transform 0.4s cubic-bezier(0.16,1,0.3,1), background 0.15s, color 0.15s" }
              : { transition: "transform 0.35s cubic-bezier(0.16,1,0.3,1), background 0.3s, color 0.3s" }),
          }}
          aria-label={t("play.yourScore")}
        >
          {t("play.scorePill", { score: currentScore })}
        </span>
      </header>
      <main className="player-game-main" style={s.main}>
        {renderContent(state, submitAnswer, t)}
      </main>
    </div>
  );
}

function renderContent(
  state: GameState,
  submitAnswer: (optionIds: string[], text?: string | null) => void,
  t: Translate,
) {
  const { phase, question, submittedOptions, result, podium, score } = state;

  if (phase === "ended" || phase === "aborted") {
    return (
      <div className="player-status-card" style={s.statusMsg} role="status">
        <p className="player-status-title">{t("play.gameOver")}</p>
        <p className="player-status-body">{t("play.thanksForPlaying")}</p>
      </div>
    );
  }

  if (phase === "podium" && podium.length > 0) {
    return <PodiumView entries={podium} t={t} />;
  }

  if (phase === "answer_reveal" && result) {
    return <ResultView result={result} t={t} />;
  }

  // No result means nothing was submitted before the question closed. Falling
  // through to the question view here would tell the player to "get ready to
  // answer" a question that is already over.
  if (phase === "answer_reveal") {
    return <NoAnswerView isSlide={question?.type === "slide"} score={score} t={t} />;
  }

  if (phase === "leaderboard") {
    return (
      <div className="player-status-card" style={s.statusMsg} role="status" aria-live="polite">
        <p className="player-status-title">{t("play.leaderboard")}</p>
        <p className="player-status-body">{t("play.yourScoreIs", { score })}</p>
      </div>
    );
  }

  if (!question) {
    return (
      <div className="player-status-card" style={s.statusMsg} role="status" aria-live="polite">
        <p className="player-status-title">{t("play.getReady")}</p>
      </div>
    );
  }

  return (
    <QuestionView
      key={question.questionRunId}
      question={question}
      phase={phase}
      submittedOptions={submittedOptions}
      phaseEndsAt={state.phaseEndsAt}
      onSubmit={submitAnswer}
      t={t}
    />
  );
}

// ---------------------------------------------------------------------------
// QuestionView
// ---------------------------------------------------------------------------

interface QuestionViewProps {
  question: QuestionEvent;
  phase: GamePhase;
  submittedOptions: string[] | null;
  phaseEndsAt: number | null;
  onSubmit: (optionIds: string[], text?: string | null) => void;
  t: Translate;
}

function QuestionView({ question, phase, submittedOptions, phaseEndsAt, onSubmit, t }: QuestionViewProps) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [typedAnswer, setTypedAnswer] = useState("");
  const answering = phase === "answering";
  const answered = submittedOptions !== null;
  const isMulti = question.type === "quiz_multi";
  const isTypeAnswer = question.type === "type_answer";
  const isSlide = question.type === "slide";

  useEffect(() => {
    setSelectedIds([]);
    setTypedAnswer("");
  }, [question.questionRunId]);

  const toggleSelected = (optionId: string) => {
    setSelectedIds((prev) =>
      prev.includes(optionId)
        ? prev.filter((id) => id !== optionId)
        : [...prev, optionId],
    );
  };

  const submitMulti = () => {
    if (selectedIds.length === 0) return;
    onSubmit(selectedIds);
  };

  const submitTyped = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = typedAnswer.trim();
    if (!trimmed) return;
    onSubmit([], trimmed);
  };

  return (
    <>
      <h1 className="player-question-prompt" style={s.prompt}>{question.prompt}</h1>
      {question.imageAssetId && (
        <img
          src={`/media/${question.imageAssetId}`}
          alt={t("play.questionImage", { prompt: question.prompt })}
          className="player-question-image"
          style={s.questionImage}
        />
      )}
      {answering && answered && (
        <p className="player-inline-status" style={{ ...s.statusMsg, padding: "0" }} role="status" aria-live="polite">
          {t("play.answerSubmitted")}
        </p>
      )}
      {answering && !answered && phaseEndsAt !== null && question.timerSeconds > 0 && (
        <CountdownRing endsAt={phaseEndsAt} totalSeconds={question.timerSeconds} size={56} />
      )}
      {!answering && !answered && (
        <p className="player-inline-status" style={{ ...s.statusMsg, padding: "0" }} role="status" aria-live="polite">
          {t("play.getReadyToAnswer")}
        </p>
      )}
      {isSlide && (
        <p className="player-status-card" style={s.statusMsg} role="status">
          {t("play.slideContinue")}
        </p>
      )}
      {isTypeAnswer && (
        <form className="player-typed-form" onSubmit={submitTyped}>
          <input
            type="text"
            value={typedAnswer}
            onChange={(e) => setTypedAnswer(e.target.value)}
            disabled={!answering || answered}
            aria-label={t("play.typeAnswer")}
            placeholder={t("play.typeAnswer")}
            autoComplete="off"
            className="player-answer-input"
            style={s.answerInput}
          />
          <button
            type="submit"
            disabled={!answering || answered || typedAnswer.trim().length === 0}
            className="player-submit-button"
            style={{
              ...s.submitBtn,
              ...(!answering || answered || typedAnswer.trim().length === 0
                ? s.disabledSubmitBtn
                : {}),
            }}
          >
            {t("play.submitAnswer")}
          </button>
        </form>
      )}
      {isMulti && (
        <p className="player-inline-status" style={{ ...s.statusMsg, padding: "0" }} role="status" aria-live="polite">
          {t("play.selectAll")}
        </p>
      )}
      <div className="answers-grid player-answer-grid" role="group" aria-label={t("play.answerOptions")}>
        {!isTypeAnswer && !isSlide && question.options.map((opt, i) => {
          const selected =
            submittedOptions?.includes(opt.id) ??
            (isMulti ? selectedIds.includes(opt.id) : false);
          const disabled = answered || !answering;
          return (
            <button
              key={opt.id}
              className="player-option-button"
              style={s.optionBtn(OPTION_COLORS[i % OPTION_COLORS.length] ?? "var(--text-mid)", selected, disabled)}
              disabled={disabled}
              aria-pressed={selected}
              onClick={() => (isMulti ? toggleSelected(opt.id) : onSubmit([opt.id]))}
            >
              <span className="player-option-letter" style={s.optionLetter} aria-hidden="true">
                <OptionShape index={i} />
              </span>
              <span className="player-option-label">{opt.label}</span>
              {(isMulti || selected) && (
                <span
                  className={`player-option-check${selected ? " is-checked" : ""}`}
                  style={s.optionCheck(selected)}
                  aria-hidden="true"
                >
                  {selected && (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M3 8.5l3.2 3.2L13 5"
                        stroke="oklch(30% 0.02 65)"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {isMulti && (
        <button
          type="button"
          disabled={!answering || answered || selectedIds.length === 0}
          className="player-submit-button"
          style={{
            ...s.submitBtn,
            ...(!answering || answered || selectedIds.length === 0 ? s.disabledSubmitBtn : {}),
          }}
          onClick={submitMulti}
        >
          {t("play.submitAnswer")}
        </button>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// ResultView
// ---------------------------------------------------------------------------

interface NoAnswerViewProps {
  isSlide: boolean;
  score: number;
  t: Translate;
}

/** Reveal screen for a player who did not get an answer in before time ran out. */
function NoAnswerView({ isSlide, score, t }: NoAnswerViewProps) {
  return (
    <div
      className="player-result-card is-incorrect"
      style={{ ...s.resultCard, ...s.resultIncorrect }}
      role="status"
      aria-live="polite"
    >
      <p className="player-result-label" style={s.resultLabel(false)}>
        {isSlide ? t("play.slide") : t("play.timesUp")}
      </p>
      <p className="player-status-body">
        {isSlide ? t("play.nothingToAnswer") : t("play.noAnswerSubmitted")}
      </p>
      <p className="player-result-total" style={s.resultTotal}>{t("play.totalScore", { score })}</p>
    </div>
  );
}

interface ResultViewProps {
  result: PlayerResultEvent;
  t: Translate;
}

function ResultView({ result, t }: ResultViewProps) {
  return (
    <div
      className={`player-result-card ${result.isCorrect ? "is-correct" : "is-incorrect"}`}
      style={{
        ...s.resultCard,
        ...(result.isCorrect ? s.resultCorrect : s.resultIncorrect),
      }}
      role="status"
      aria-live="polite"
    >
      <p className="player-result-label" style={s.resultLabel(result.isCorrect)}>
        {result.isCorrect ? t("play.correct") : t("play.incorrect")}
      </p>
      <p className="player-result-points" style={s.resultPoints} aria-label={t("play.pointsEarned", { points: result.points })}>
        +{result.points}
      </p>
      {result.isCorrect && (result.streak ?? 0) >= 2 && (
        <p className="player-streak-banner">
          {t("play.streak", { count: result.streak ?? 0 })}
          {(result.streakBonus ?? 0) > 0 && (
            <span className="player-streak-bonus"> {t("play.streakBonus", { bonus: result.streakBonus ?? 0 })}</span>
          )}
        </p>
      )}
      <p className="player-result-total" style={s.resultTotal}>{t("play.totalScore", { score: result.runningTotal })}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PodiumView
// ---------------------------------------------------------------------------

interface PodiumViewProps {
  entries: PodiumEntry[];
  t: Translate;
}

function PodiumView({ entries, t }: PodiumViewProps) {
  return (
    <>
      <h1
        className="player-question-prompt player-podium-title"
        style={{
          ...s.prompt,
          paddingTop: "var(--space-sm)",
          animation: "slide-up-in 280ms cubic-bezier(0.16, 1, 0.3, 1) both",
        }}
      >
        {t("play.finalResults")}
      </h1>
      <ol className="player-podium-list" style={s.podiumList} aria-label={t("play.finalLeaderboard")}>
        {entries.slice(0, 10).map((entry, i) => (
          <li
            key={entry.playerId}
            className={`player-podium-row rank-${Math.min(entry.rank, 4)}`}
            style={{
              ...s.podiumRow(entry.rank),
              animation: `slide-up-in 280ms cubic-bezier(0.16, 1, 0.3, 1) ${(i + 1) * 45}ms both`,
            }}
          >
            <span className="player-podium-rank" style={s.podiumRank}>#{entry.rank}</span>
            <span className="player-podium-name" style={{
              ...s.podiumNickname,
              fontSize: entry.rank === 1 ? "var(--text-base)" : "var(--text-sm)",
              fontWeight: entry.rank === 1 ? 800 : 600,
            }}>{entry.nickname}</span>
            <span className="player-podium-score" style={{
              ...s.podiumScore,
              fontSize: entry.rank === 1 ? "var(--text-lg)" : "var(--text-base)",
            }}>{entry.score} pts</span>
          </li>
        ))}
      </ol>
    </>
  );
}
